import type { Table } from "@tanstack/react-table";
import { flexRender } from "@tanstack/react-table";

import { useGetUserProfileAPI } from "@/api/userProfile/useUserProfileAPI";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Table as TableUI,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "@/components/ui/table";
import Tooltip from "@/components/ui/tooltip";
import { TOOLTIP_TRIGGER_LENGTH } from "@/types/common";
import { DatasetDataRowValue } from "@/types/dataset";
import { formatRenderValue } from "@/utils/string";

import { twMerge } from "tailwind-merge";

import { ExtendedColumnDef } from "./columns";

interface TableDataProps<TData> {
  table: Table<TData>;
  emptyDataMessage?: string | React.ReactNode;
}

export default function TableData<TData>({
  table,
  emptyDataMessage
}: Readonly<TableDataProps<TData>>) {
  const { data } = useGetUserProfileAPI();

  return (
    <ScrollArea className="h-full border border-gainsboro rounded-xl">
      <TableUI
        className="relative table-fixed border-separate border-spacing-0"
        role="grid"
      >
        <TableHeader className="z-50">
          {table.getHeaderGroups().map((headerGroup, index) => (
            <TableRow
              key={`${headerGroup.id}-${index}`}
              role="row"
              className="border-b-0"
            >
              {headerGroup.headers.map((header, index) => {
                const isPinned = header.column.getIsPinned();
                return (
                  <TableHead
                    key={`${header.id}-${index}`}
                    className={twMerge(
                      "h-14 bg-white-smoke py-2 px-3 w-[200px] z-20 !border-b",
                      header.index === 0 && "border-l-0 rounded-tl-lg",
                      header.index === headerGroup.headers.length - 1 &&
                        "rounded-tr-lg",
                      isPinned && "sticky right-0 !border-l"
                    )}
                    role="columnheader"
                    scope="col"
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </TableHead>
                );
              })}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody className="max-h-[522px]">
          {table.getRowModel().rows?.length > 0 ? (
            table.getRowModel().rows.map((row, rowIndex) => (
              <TableRow
                key={`${row.id}-${rowIndex}`}
                data-state={row.getIsSelected() && "selected"}
                aria-selected={row.getIsSelected()}
                role="row"
              >
                {row.getVisibleCells().map((cell, index) => {
                  // FIXME: This is a workaround for the type issue with cell.getValue()
                  // Ideally, we should have a more robust type system in place. Will update this later.
                  const value = cell.getValue() as DatasetDataRowValue;
                  const isPinned = cell.column.getIsPinned();

                  const isEmpty =
                    value === null ||
                    value === undefined ||
                    value === "" ||
                    (typeof value === "number" && Number.isNaN(value)) ||
                    (typeof value === "object" &&
                      value !== null &&
                      Object.keys(value).length === 0);

                  const isFirstCell = row.getVisibleCells()[0]?.id === cell.id;
                  const isLastCell =
                    row.getVisibleCells()[row.getVisibleCells().length - 1]
                      ?.id === cell.id;

                  const isTooltipDisabled = !(
                    typeof value === "string" &&
                    value.length > TOOLTIP_TRIGGER_LENGTH
                  );

                  // FIXME: This is a workaround for the type issue with cell.column.columnDef.type
                  // Ideally, we should have a more robust type system in place. Will update this later
                  const columnType = (
                    cell.column.columnDef as ExtendedColumnDef<TData, unknown>
                  )?.type;

                  const content = columnType
                    ? formatRenderValue(columnType, value, data?.timezone)
                    : flexRender(cell.column.columnDef.cell, cell.getContext());

                  return (
                    <TableCell
                      key={`${cell.id}-${index}`}
                      className={twMerge(
                        "py-2 px-3 !border-b",
                        isEmpty && "bg-white-smoke",
                        isFirstCell && "border-l-0",
                        isLastCell && "border-r-0",
                        isPinned &&
                          "sticky right-0 !border-l bg-white shadow-[-15px_0px_38px_-22px_rgba(0,0,0,0.75)]"
                      )}
                    >
                      <Tooltip
                        content={content}
                        position="bottom-center"
                        className="z-[1000] text-left w-fit bg-neutral-800 max-w-screen-sm break-words whitespace-pre-wrap"
                        arrowClassName="fill-neutral-800"
                        disable={isTooltipDisabled}
                      >
                        <div
                          className={twMerge(
                            "max-w-[200px] line-clamp-2 text-ellipsis",
                            !isTooltipDisabled && "cursor-pointer"
                          )}
                        >
                          {content}
                        </div>
                      </Tooltip>
                    </TableCell>
                  );
                })}
              </TableRow>
            ))
          ) : (
            <TableRow className="hover:bg-transparent">
              <TableCell
                colSpan={table.getAllColumns().length}
                className="text-center h-[calc(100vh-400px)]"
              >
                {emptyDataMessage}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </TableUI>
    </ScrollArea>
  );
}
